/**
 * 烛民(方案 C4):水滴剪影 + 胸口烛光,五种程序动画,boids-lite 群体AI,夜晚归屋/守夜者。
 */
import { Container, Sprite } from 'pixi.js';
import { Stage, LW } from '../engine/Stage';
import { sky } from '../engine/SkyRenderer';
import { clock, world as windSrc } from '../engine/Ticker';
import { TEX } from '../engine/Tex';
import { P, darken } from '../engine/Palette';
import { easing } from '../engine/Easing';
import { rand, pick } from '../engine/util';

type Anim = 'idle' | 'walk' | 'cheer' | 'pray' | 'dance';
type Mode = 'wander' | 'seek' | 'social';

class Citizen {
  root = new Container();
  private body: Sprite;
  private core: Sprite;
  private halo: Sprite;
  x: number;
  targetX: number;
  vx = 0;
  mode: Mode = 'wander';
  anim: Anim = 'idle';
  animT = 999;
  ph = rand(0, 6.28);
  poiX: number | null = null;
  socialT = 0;
  home = false;
  danceCenter = 0;

  constructor(x: number, private baseY: number) {
    this.x = x; this.targetX = x;
    this.body = new Sprite(TEX.teardrop());
    this.body.anchor.set(0.5, 1);
    this.body.scale.set(22 / 88); // 目标高 22px
    this.core = new Sprite(TEX.core());
    this.core.anchor.set(0.5); this.core.tint = P.emberHi; this.core.scale.set(0.05); this.core.y = -13;
    this.halo = new Sprite(TEX.glow());
    this.halo.anchor.set(0.5); this.halo.blendMode = 'add'; this.halo.tint = P.ember; this.halo.alpha = 0.4;
    this.halo.scale.set(0.14); this.halo.y = -13;
    this.root.addChild(this.body, this.halo, this.core);
    this.root.position.set(x, baseY);
  }

  setAnim(a: Anim, durSec = 3) { this.anim = a; this.animT = durSec; }

  update(dt: number, t: number, sil: number) {
    this.root.visible = !this.home;
    if (this.home) return;
    this.body.tint = darken(sil, 0.2);

    if (this.animT > 0) this.animT -= dt;
    else if (this.anim !== 'idle' && this.anim !== 'walk') this.anim = 'idle';

    // 位移(AI)
    if (this.anim === 'cheer' || this.anim === 'pray' || this.anim === 'dance') {
      // 表演时原地
    } else if (this.mode === 'seek' && this.poiX != null) {
      const d = this.poiX - this.x;
      if (Math.abs(d) > rand(30, 60)) { this.walkTo(this.poiX, dt); } else { this.vx *= 0.8; this.anim = 'idle'; }
    } else if (this.mode === 'social') {
      this.vx *= 0.8; this.anim = 'idle';
      this.socialT -= dt; if (this.socialT <= 0) this.mode = 'wander';
    } else {
      if (Math.abs(this.x - this.targetX) < 5 || Math.random() < 0.004) this.targetX = rand(LW * 0.28, LW * 0.72);
      this.walkTo(this.targetX, dt);
    }
    this.x += this.vx * dt;
    this.x = Math.max(LW * 0.26, Math.min(LW * 0.74, this.x));
    this.root.x = this.x;

    this.applyAnim(t);
  }

  private walkTo(tx: number, dt: number) {
    const spd = 18;
    this.vx += (Math.sign(tx - this.x) * spd - this.vx) * Math.min(1, dt * 4);
    if (Math.abs(this.vx) > 2) this.anim = 'walk';
  }

  private applyAnim(t: number) {
    const b = this.body;
    let bob = 0, rot = 0, sqY = 1, sqX = 1, haloScale = 0.14, haloA = 0.4;
    const nod = () => Math.sin(t * 5 + this.ph);
    switch (this.anim) {
      case 'idle': bob = Math.sin((t / 3) * 6.28 + this.ph) * 1; break;
      case 'walk':
        bob = Math.abs(Math.sin(t * 12.6 + this.ph)) * 2;
        rot = Math.sign(this.vx) * 0.07 * Math.sin(t * 12.6);
        sqY = 0.95 + 0.05 * Math.sin(t * 12.6); sqX = 2 - sqY; break;
      case 'cheer':
        bob = -Math.abs(Math.sin(t * 9 + this.ph)) * 6 * easing.backOut(Math.min(1, (t * 9) % 1));
        haloScale = 0.22; haloA = 0.6; break;
      case 'pray':
        sqY = 0.8; sqX = 1.08; bob = 0; haloScale = 0.12; haloA = 0.3 + 0.1 * Math.sin(t / 6 * 6.28); break;
      case 'dance':
        this.root.x = this.danceCenter + Math.cos(t * 3 + this.ph) * 14;
        bob = Math.abs(Math.sin(t * 6 + this.ph)) * 5; rot = 0.2 * nod(); haloScale = 0.18; break;
    }
    this.root.y = this.baseY + bob;
    b.rotation = rot; b.scale.set((22 / 88) * sqX, (22 / 88) * sqY);
    this.halo.scale.set(haloScale); this.halo.alpha = haloA;
    this.core.tint = P.emberHi;
    void windSrc;
  }

  setBase(y: number) { this.baseY = y; }
}

export class CitizenCrowd {
  private list: Citizen[] = [];
  private t = 0;
  private layer: Container;
  private surfaceYAt: (x: number) => number;
  private nightChecked = -1;

  constructor(stage: Stage, surfaceYAt: (x: number) => number) {
    this.layer = stage.layers.mainIsland;
    this.surfaceYAt = surfaceYAt;
  }

  get count() { return this.list.length; }

  setPopulation(n: number) {
    n = Math.max(0, Math.min(n, 120));
    while (this.list.length < n) this.spawn();
    while (this.list.length > n) { const c = this.list.pop()!; this.layer.removeChild(c.root); }
  }

  private spawn(fromSky = false): Citizen {
    const x = rand(LW * 0.3, LW * 0.7);
    const c = new Citizen(x, this.surfaceYAt(x));
    if (fromSky) c.root.y = -rand(40, 200);
    this.layer.addChild(c.root);
    this.list.push(c);
    return c;
  }

  broadcast(anim: Anim, durSec = 3, ratio = 1) {
    for (const c of this.list) if (Math.random() < ratio) c.setAnim(anim, durSec);
  }
  cheer(durSec = 3) { this.broadcast('cheer', durSec); }
  pray(durSec = 4) { this.broadcast('pray', durSec); }
  dance(durSec = 20) {
    for (const c of this.list) { c.setAnim('dance', durSec); c.danceCenter = c.x; }
  }
  /** POI 出现:60% 烛民前往围观 */
  addPOI(x: number, durSec = 6) {
    for (const c of this.list) if (Math.random() < 0.6) { c.mode = 'seek'; c.poiX = x; c.setAnim('walk', 0.1); setTimeout(() => { c.mode = 'wander'; }, durSec * 1000); }
  }
  /** 天空撕裂:分两群 */
  splitTwo() {
    this.list.forEach((c, i) => { c.mode = 'seek'; c.poiX = i % 2 ? LW * 0.66 : LW * 0.34; });
  }
  gather(x: number, durSec = 6) { this.addPOI(x, durSec); }
  reset() { for (const c of this.list) { c.mode = 'wander'; c.poiX = null; } }
  spawnFromSky(n: number) { for (let i = 0; i < n; i++) this.spawn(true); }

  update(dt: number) {
    this.t += dt;
    const sil = sky.silhouette;
    // 相遇 socialize(抽样,省算力)
    if (Math.random() < 0.05 && this.list.length > 2) {
      const a = pick(this.list);
      if (a.mode === 'wander' && Math.random() < 0.2) { a.mode = 'social'; a.socialT = 3; }
    }
    // 夜晚归屋/守夜者
    this.applyNight();
    for (const c of this.list) c.update(dt, this.t, sil);
  }

  private applyNight() {
    const h = Math.floor(clock.hour());
    if (h === this.nightChecked) return;
    this.nightChecked = h;
    const n = this.list.length;
    if (h >= 22 || h < 5) {
      const keep = (h >= 0 && h < 5) ? Math.min(5, n) : Math.floor(n * 0.7);
      this.list.forEach((c, i) => { c.home = i >= keep; });
    } else {
      for (const c of this.list) c.home = false;
    }
  }
}
